import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { Card } from "../../../components/ui/Card";
import { MasterDetailLayout } from "../../../components/ui/MasterDetailLayout";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui/State";
import { type Column, Table } from "../../../components/ui/Table";
import { ApiError } from "../../../lib/api";
import type {
  DestinationBucket,
  DeviceBucket,
  RuleBucket,
} from "../../../lib/api/network/violations";
import { networkViolationsApi } from "../../../lib/api/network/violations";
import { formatDateTime } from "../../../lib/format";
import { t } from "../../../lib/i18n";
import { useInsightsScope } from "../../../lib/useInsightsScope";
import { PivotDrillDownPanel } from "./PivotDrillDownPanel";

const BUCKET_LIMIT = 100;

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t("errors." + err.code, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function DestinationsBucketsTab() {
  const scope = useInsightsScope();
  const [params, setParams] = useSearchParams();
  const detail = params.get("detail");

  const query = useQuery({
    queryKey: ["violations", "group-by", "destination", scope],
    queryFn: () => networkViolationsApi.groupByDestinations(scope, BUCKET_LIMIT),
  });

  const openDetail = (destination: string) => {
    const next = new URLSearchParams(params);
    next.set("detail", destination);
    setParams(next, { replace: false });
  };

  const closeDetail = () => {
    const next = new URLSearchParams(params);
    next.delete("detail");
    setParams(next, { replace: false });
  };

  const columns: Column<DestinationBucket>[] = [
    {
      key: "destination",
      header: t("insights.violations.column.destination"),
      render: (row) => (
        <button
          type="button"
          onClick={() => openDetail(row.key)}
          className="app-name app-name--link"
          style={{
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
            color: "var(--color-accent)",
            font: "inherit",
            textAlign: "left",
          }}
        >
          <span className="mono">{row.label}</span>
        </button>
      ),
    },
    {
      key: "kind",
      header: t("insights.violations.column.kind"),
      render: (row) => <span className="badge">{row.destination_type}</span>,
    },
    {
      key: "total",
      header: t("insights.violations.column.attempts"),
      render: (row) => <span className="num">{row.total_attempts.toLocaleString()}</span>,
      align: "right",
    },
    {
      key: "devices",
      header: t("insights.violations.column.distinctDevices"),
      render: (row) => <span className="num">{row.distinct_devices.toLocaleString()}</span>,
      align: "right",
    },
    {
      key: "last",
      header: t("insights.violations.column.lastSeen"),
      render: (row) => (
        <span className="mono table-cell--datetime">
          {formatDateTime(row.last_attempted_at)}
        </span>
      ),
    },
  ];

  return (
    <MasterDetailLayout
      list={<BucketsCard query={query} columns={columns} rowKey={(row) => row.key} />}
      detail={
        detail ? (
          <PivotDrillDownPanel
            dimension="destination"
            value={detail}
            onClose={closeDetail}
          />
        ) : null
      }
      onClose={closeDetail}
      ariaLabel={t("insights.violations.detail.eyebrow")}
    />
  );
}

export function DevicesBucketsTab() {
  const scope = useInsightsScope();
  const query = useQuery({
    queryKey: ["violations", "group-by", "device", scope],
    queryFn: () => networkViolationsApi.groupByDevices(scope, BUCKET_LIMIT),
  });

  const columns: Column<DeviceBucket>[] = [
    {
      key: "device",
      header: t("insights.violations.column.device"),
      render: (row) => (
        <Link to={"/devices?id=" + encodeURIComponent(row.key)}>
          <span className="mono">{row.device_uid || row.key}</span>
        </Link>
      ),
    },
    {
      key: "platform",
      header: t("insights.violations.column.platform"),
      render: (row) =>
        row.platform ? (
          <span className="badge badge--info">
            {t("common.platform." + row.platform, row.platform)}
          </span>
        ) : (
          "-"
        ),
    },
    {
      key: "model",
      header: t("insights.violations.column.model"),
      render: (row) => row.model || "-",
    },
    {
      key: "total",
      header: t("insights.violations.column.attempts"),
      render: (row) => <span className="num">{row.total_attempts.toLocaleString()}</span>,
      align: "right",
    },
    {
      key: "destinations",
      header: t("insights.violations.column.distinctDestinations"),
      render: (row) => (
        <span className="num">{row.distinct_destinations.toLocaleString()}</span>
      ),
      align: "right",
    },
    {
      key: "last",
      header: t("insights.violations.column.lastSeen"),
      render: (row) => (
        <span className="mono table-cell--datetime">
          {formatDateTime(row.last_attempted_at)}
        </span>
      ),
    },
  ];

  return <BucketsCard query={query} columns={columns} rowKey={(row) => row.key} />;
}

export function RulesBucketsTab() {
  const scope = useInsightsScope();
  const [params, setParams] = useSearchParams();
  const detail = params.get("detail");
  const detailLabel = params.get("label") ?? undefined;

  const query = useQuery({
    queryKey: ["violations", "group-by", "rule", scope],
    queryFn: () => networkViolationsApi.groupByRules(scope, BUCKET_LIMIT),
  });

  const openDetail = (key: string, label: string) => {
    const next = new URLSearchParams(params);
    next.set("detail", key);
    next.set("label", label);
    setParams(next, { replace: false });
  };
  const closeDetail = () => {
    const next = new URLSearchParams(params);
    next.delete("detail");
    next.delete("label");
    setParams(next, { replace: false });
  };

  const columns: Column<RuleBucket>[] = [
    {
      key: "rule",
      header: t("insights.violations.column.rule"),
      render: (row) =>
        row.key ? (
          <button
            type="button"
            onClick={() => openDetail(row.key!, row.label)}
            className="app-name--link"
            style={{
              background: "none",
              border: 0,
              padding: 0,
              cursor: "pointer",
              color: "var(--color-accent)",
              font: "inherit",
              textAlign: "left",
            }}
          >
            {row.label}
          </button>
        ) : (
          <span className="muted">{row.label}</span>
        ),
    },
    {
      key: "total",
      header: t("insights.violations.column.attempts"),
      render: (row) => <span className="num">{row.total_attempts.toLocaleString()}</span>,
      align: "right",
    },
    {
      key: "destinations",
      header: t("insights.violations.column.distinctDestinations"),
      render: (row) => (
        <span className="num">{row.distinct_destinations.toLocaleString()}</span>
      ),
      align: "right",
    },
    {
      key: "devices",
      header: t("insights.violations.column.distinctDevices"),
      render: (row) => <span className="num">{row.distinct_devices.toLocaleString()}</span>,
      align: "right",
    },
    {
      key: "last",
      header: t("insights.violations.column.lastSeen"),
      render: (row) => (
        <span className="mono table-cell--datetime">
          {formatDateTime(row.last_attempted_at)}
        </span>
      ),
    },
  ];

  return (
    <MasterDetailLayout
      list={
        <BucketsCard
          query={query}
          columns={columns}
          rowKey={(row) => row.key ?? "_null"}
        />
      }
      detail={
        detail ? (
          <PivotDrillDownPanel
            dimension="rule"
            value={detail}
            label={detailLabel}
            onClose={closeDetail}
          />
        ) : null
      }
      onClose={closeDetail}
      ariaLabel={t("insights.violations.detail.eyebrow.rule")}
    />
  );
}

interface BucketsCardProps<T> {
  query: { isLoading: boolean; error: unknown; data: { items: T[] } | undefined };
  columns: Column<T>[];
  rowKey: (row: T) => string;
}

function BucketsCard<T>({ query, columns, rowKey }: BucketsCardProps<T>) {
  return (
    <Card padding="none">
      {query.isLoading && <LoadingState />}
      {query.error != null && (
        <ErrorState titleKey="states.errorTitle" message={translateError(query.error)} />
      )}
      {query.data && (
        <Table
          rows={query.data.items}
          columns={columns}
          rowKey={rowKey}
          empty={
            <EmptyState
              titleKey="insights.violations.empty.title"
              bodyKey="insights.violations.empty.body"
            />
          }
        />
      )}
    </Card>
  );
}
